import { CSSProperties, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import { createPortal } from "react-dom";
import { ContextMenuAiModeProps, ContextMenuAreaSelectionProps, ContextMenuBaseImageProps, ContextMenuEmptyBaseProps, ContextMenuImageGalleryProps, ContextMenuLayerProps, ContextMenuLayerSidebarProps, ContextMenuLayerSidebarVariationsProps, ContextMenuProps } from "../../../types/imageStudio/contextMenu";
import EffectThemeDropdown from "../../effectTheme/effectThemeDropdown";
import EffectThemeContainer from "../../effectTheme/effectThemeContainer";
import { downloadScaleList, downloadScaleTypes, ExportFormat } from "../../../utils/images/imageConvertFormats";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRightArrowLeft, faArrowRotateBack, faArrowRotateForward, faArrowsRotate, faBackwardStep, faBullseye, faComment, faCrop, faCropSimple, faDiagramProject, faDownload, faEye, faEyeSlash, faForwardStep, faHexagonNodes, faImage, faLayerGroup, faMagnifyingGlassMinus, faMagnifyingGlassPlus, faObjectGroup, faPaintBrush, faPerson, faPlaneUp, faPlus, faPowerOff, faShapes, faT, faTrashCan, faUpRightAndDownLeftFromCenter, faWandMagic, faX } from "@fortawesome/free-solid-svg-icons";
import { faCircle, faCopy, faFileImage, faFilePdf, faPenToSquare, faSquare, faSquarePlus } from "@fortawesome/free-regular-svg-icons";
import AILogo from "../icons/AILogo";
import { isFeatureEnabled } from "../../../features";
import { StageBadge } from "../../ui/StageBadge";

export const ContextMenu = ({
  wrapperRef,
  contextMenu,
  selectedLayer=null,
  baseImage,
  layerareaSelection,
  selectedLayerIDs=[],
  allLayer=[],
  canRedoLayer=false,
  canUndoLayer=false,
  workOnBaseImage,
  selectionDisabled,
  upscaleCosts,
  addBaseImageasLayerAndSetImageAsTransparentBackground,
  onChangeMode,
  onAddLayer,
  onConvertToImageLayer,
  onCreateVariations,
  onCropSelectedLayerSave,
  onDeleteSelectedAreaFromLayer,
  onDeleteSelectedLayer,
  onDuplicateSelectedLayer,
  onDuplicateSelectedAreaFromLayer,
  onExportImage,
  downloadActiveInput,
  onExtractObject,
  onExtractPose,
  onSpellCorrection,
  onExtractSelectedAreaToNewLayer,
  onFlipHorizontalSelectedLayer,
  onFlipHorizontalBaseImage,
  onFlipVerticalBaseImage,
  onFlipVerticalSelectedLayer,
  onMergeLayerWithPrevious,
  onMergeSelectedLayer,
  onRedoBaseImage,
  onRedoSelectedLayerContentChange,
  onRemoveBackground,
  onRemoveObject,
  onResetBaseImage,
  onRotateBaseImage,
  onRotateSelectedLayer,
  onUndoBaseImage,
  onUndoSelectedLayerContentChange,
  onUpscale,
  onWriteAllLayerToBaseImage,
  onWriteSelectedLayerToBaseImage,
  onZoomIn,
  onZoomOut,
  setContextMenu,
  onCropBaseImage,
  onHideSelectedLayer,
  onExpandBaseTransparent,
  onExpandBaseWithVariations,
  onExpandLayerTransparent,
  onExpandLayerWithVariations,
  onChangeLayerToBack,
  onChangeLayerToFront,
}: ContextMenuProps) => {
    const { t } = useTranslation('imageStudioToolbar');
    const menuRef = useRef<HTMLDivElement | null>(null);
    const rotateList = [90, 180, 270];
    const exportFormatList: ExportFormat [] = ['png','jpg','pdf','gif','tif','svg'];

    const [menuPlacement, setMenuPlacement] = useState({
        x: contextMenu.x,
        y: contextMenu.y,
        openLeft: false,
        openUp: false
    });
    const [downloadRequest, setDownloadRequest] = useState<{
        format: ExportFormat;
        scale: downloadScaleTypes;
    } | null>(null);

    const closeContextMenu = () => {
        setContextMenu((prev) => ({ ...prev, visible: false }));
    };

    const runAndClose = (callback?: () => void | Promise<void>) => {
        void callback?.();
        closeContextMenu();
    };
    useEffect(() => {
        const onMouseDown = (e: MouseEvent) => {
        if (menuRef.current?.contains(e.target as Node)) return;
        closeContextMenu();
        };

        const onKeyDown = (e: KeyboardEvent) => {
        if (e.key === "Escape") closeContextMenu();
        };

        window.addEventListener("mousedown", onMouseDown);
        window.addEventListener("keydown", onKeyDown);

        return () => {
        window.removeEventListener("mousedown", onMouseDown);
        window.removeEventListener("keydown", onKeyDown);
        };
    }, []);

    useEffect(() => {
        const el = wrapperRef.current;
        if (!el) return;

        const onContextMenu = (e: MouseEvent) => {
        e.preventDefault();

            const rect = el.getBoundingClientRect();

            setContextMenu({
                visible: true,
                x: e.clientX,
                y: e.clientY
            });
        };

        el.addEventListener("contextmenu", onContextMenu);

        return () => {
        el.removeEventListener("contextmenu", onContextMenu);
        };
    }, [wrapperRef]);
    useLayoutEffect(() => {
        if (!contextMenu.visible) return;

        const menu = menuRef.current;
        if (!menu) return;

        const padding = 8;
        const submenuWidth = 240;

        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        const menuWidth = menu.offsetWidth;
        const menuHeight = menu.offsetHeight;

        const openLeft =
            contextMenu.x + menuWidth + submenuWidth + padding > viewportWidth;

        const openUp =
            contextMenu.y + menuHeight + padding > viewportHeight;

        const nextX = Math.max(
            padding,
            Math.min(contextMenu.x, viewportWidth - menuWidth - padding)
        );

        const nextY = Math.max(
            padding,
            Math.min(contextMenu.y, viewportHeight - menuHeight - padding)
        );

        setMenuPlacement({
            x: nextX,
            y: nextY,
            openLeft,
            openUp
        });
        }, [contextMenu.visible, contextMenu.x, contextMenu.y]);
    useEffect(() => {
        if (!downloadRequest) return;

        void onExportImage?.(downloadRequest.format, downloadRequest.scale);
        setDownloadRequest(null);
    }, [downloadRequest]);

    if (!contextMenu.visible) return null;

    const menuElement = (
        <div
            ref={menuRef}
            className={[
                "context-menu-position",
                menuPlacement.openLeft ? "context-menu-open-left" : "",
                menuPlacement.openUp ? "context-menu-open-up" : ""
            ].join(" ")}
            style={{
                left: menuPlacement.x,
                top: menuPlacement.y
            }}
            onContextMenu={(e) => e.preventDefault()}
            onMouseDown={(e) => e.stopPropagation()}
        >
        <EffectThemeContainer containerClass="context-menu-container">
            <>
            {baseImage && (
                <ContextMenuBaseImage
                baseImage={baseImage}
                allLayer={allLayer}
                selectedLayerIDs={selectedLayerIDs}
                rotateList={rotateList}
                onFlipHorizontalBaseImage={() => runAndClose(onFlipHorizontalBaseImage)}
                onFlipVerticalBaseImage={() => runAndClose(onFlipVerticalBaseImage)}
                onRedoBaseImage={() => runAndClose(onRedoBaseImage)}
                onResetBaseImage={() => runAndClose(onResetBaseImage)}
                onRotateBaseImage={(degrees) => {
                    onRotateBaseImage?.(degrees);
                    closeContextMenu();
                }}
                onUndoBaseImage={() => runAndClose(onUndoBaseImage)}
                onWriteAllLayerToBaseImage={() => runAndClose(onWriteAllLayerToBaseImage)}
                onZoomIn={() => runAndClose(onZoomIn)}
                onZoomOut={() => runAndClose(onZoomOut)}
                onCropBaseImage={() => runAndClose(onCropBaseImage)}
                onWriteSelectedLayerToBaseImage={() => runAndClose(onWriteSelectedLayerToBaseImage)}
                addBaseImageasLayerAndSetImageAsTransparentBackground={addBaseImageasLayerAndSetImageAsTransparentBackground}
                />
            )}
            {
                !selectionDisabled
                ?   <ContextMenuSubmenu 
                        label={<span>
                            <FontAwesomeIcon
                                icon={faBullseye}
                            className="me-2"
                            />
                            {t('menu.selectArea')} {selectedLayer ? `(${t('label.layer')})` : `(${t('label.base')})`}
                        </span>}
                    >
                        <ContextMenuItem onClick={() => runAndClose(() => onChangeMode?.("select-rect"))}>
                            <span>
                                <FontAwesomeIcon
                                    icon={faSquare}
                                    className="me-2"
                                    size="sm"
                                />
                                {t('menu.rectangle')}
                            </span>
                        </ContextMenuItem>
                        <ContextMenuItem onClick={() => runAndClose(() => onChangeMode?.("select-oval"))}>
                            <span>
                                <FontAwesomeIcon
                                    icon={faCircle}
                                    className="me-2"
                                    size="sm"
                                />
                                {t('menu.oval')}
                            </span>
                        </ContextMenuItem>
                        <ContextMenuItem onClick={() => runAndClose(() => onChangeMode?.("select-free"))}>
                            <span>
                                <FontAwesomeIcon
                                    icon={faBullseye}
                                    className="me-2"
                                    size="sm"
                                />
                                {t('menu.free')}
                            </span>
                        </ContextMenuItem>
                    </ContextMenuSubmenu>
                :   <></>
            }

            <ContextMenuSubmenu 
                label={<span>
                    <FontAwesomeIcon
                        icon={faSquarePlus}
                        className="me-2"
                    />
                    {t('label.addLayer')}
                </span>}
            >
                <ContextMenuItem onClick={() => runAndClose(() => onAddLayer?.('image'))}>
                    <span>
                        <FontAwesomeIcon
                            icon={faImage}
                            className="me-2"
                            size="sm"
                        />
                        {t('label.image')}
                    </span>
                </ContextMenuItem>
                <ContextMenuItem onClick={() => runAndClose(() => onAddLayer?.('text'))}>
                    <span>
                        <FontAwesomeIcon
                            icon={faT}
                            className="me-2"
                            size="sm"
                        />
                        Text
                    </span>
                </ContextMenuItem>
                <ContextMenuItem onClick={() => runAndClose(() => onAddLayer?.('drawings'))}>
                    <span>
                        <FontAwesomeIcon
                            icon={faPaintBrush}
                            className="me-2"
                            size="sm"
                        />
                        {t('menu.drawings')}
                    </span>
                </ContextMenuItem>
            </ContextMenuSubmenu>
            {   !selectedLayer || (selectedLayer && selectedLayer.type !== 'text')
                ?   <ContextMenuSubmenu 
                        label={<span>
                            <FontAwesomeIcon
                                icon={faSquarePlus}
                                className="me-2"
                            />
                            {selectedLayerIDs.length === 1 ? t('menu.expandLayer') : t('menu.expandBase')}
                        </span>}
                    >
                        <ContextMenuItem onClick={selectedLayerIDs.length === 1 ? () => runAndClose(() => onExpandLayerTransparent?.()) : () => runAndClose(() => onExpandBaseTransparent?.())}>
                            <span>
                                <FontAwesomeIcon
                                    icon={faImage}
                                    className="me-2"
                                    size="sm"
                                />
                                {t('menu.transparent')}
                            </span>
                        </ContextMenuItem>
                        { isFeatureEnabled('imageOutpaint') && ((!selectedLayer && baseImage) ||(selectedLayer && selectedLayerIDs.length === 1 && (selectedLayer.type === 'ai-variation' || selectedLayer.type === 'image')))
                            ?   <ContextMenuItem onClick={selectedLayerIDs.length === 1 ? () => runAndClose(() => onExpandLayerWithVariations?.()) : () => runAndClose(() => onExpandBaseWithVariations?.())}>
                                    <span>
                                        <FontAwesomeIcon
                                            icon={faObjectGroup}
                                            className="me-2"
                                            size="sm"
                                        />
                                        {t('menu.withVariations')}<StageBadge feature="imageOutpaint"/>
                                    </span>
                                </ContextMenuItem>
                            : <></>
                        }
                    </ContextMenuSubmenu>
                :   <></>

            }
            <ContextMenuSubmenu 
                style={{width: '250px'}}
                label={<span>
                    <FontAwesomeIcon
                        icon={faDownload}
                        className="me-2"
                    />
                    {t('menu.download')}
                </span>}
            >
                <div className="row p-1 m-1 mb-0 pb-0">
                    <div className="col m-0 p-0 mb-1 text-small text-muted">
                        {t('label.format')}
                    </div>
                    <div className="col m-0 p-0 mb-1 text-small text-muted">
                        {t('menu.upscale')}
                        {/* <p className="m-0 p-0 text-secondary">2x: {upscaleCosts?.upscale2x ?? '—'} {t('coins')}</p>
                        <p className="m-0 p-0 text-secondary">4x: {upscaleCosts?.upscale4x ?? '—'} {t('coins')}</p> */}
                    </div>
                </div>
                {exportFormatList.map((item:ExportFormat) => {
                    const format = item as ExportFormat;

                    return (
                        <div className="p-0 m-0">
                            <ContextMenuItem
                                onClick={() => setDownloadRequest({ format, scale: "1x" })}
                                disabled= {(downloadActiveInput?.some(item => item.type === format && item.scale === "1x")) ?? false}
                            >
                                <div className="row w-100 mb-1">
                                    <div className="col-6 m-0 p-0 ps-2">
                                        <span>
                                            <FontAwesomeIcon
                                                className="me-2"
                                                size="sm"
                                                icon={item!== 'pdf' ? faFileImage : faFilePdf}
                                            />
                                            .{item}
                                        </span>
                                    </div>
                                    <div className="col-6 m-0 p-0 ps-2">
                                        {(format === "png" || format === "jpg") && (
                                            <div
                                                className="btn-group btn-group-sm p-0"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                {downloadScaleList.map((scale) => {
                                                    const disabled = (downloadActiveInput?.some(item => item.type === format && item.scale === scale)) ?? false;
                                                    return (
                                                        <button
                                                            key={scale}
                                                            type="button"
                                                            className="btn btn-outline-secondary p-1 m-0"
                                                            disabled= {disabled}
                                                            aria-disabled={disabled}
                                                            style={{
                                                                cursor: disabled
                                                                    ? "arrow"
                                                                    : "pointer",
                                                                opacity: disabled
                                                                    ? 0.55
                                                                    : 1
                                                            }}
                                                            onClick={(event) => {
                                                                event.preventDefault();
                                                                event.stopPropagation();

                                                                if (disabled) return;
                                                                setDownloadRequest({ format, scale });
                                                            }}
                                                        >
                                                            {scale}
                                                        </button>
                                                )})}
                                            </div>
                                        )}
                                    </div>

                                    
                                </div>
                            </ContextMenuItem>
                        </div>
                    );
                })}
            </ContextMenuSubmenu>

            {(selectedLayer || selectedLayerIDs.length > 0)  &&(
                <ContextMenuLayer
                rotateList={rotateList}
                selectedLayerIDs={selectedLayerIDs}
                onDeleteSelectedLayer={() => runAndClose(onDeleteSelectedLayer)}
                onDuplicateSelectedLayer={() => runAndClose(onDuplicateSelectedLayer)}
                onCropSelectedLayerSave={() => runAndClose(onCropSelectedLayerSave)}
                onUndoSelectedLayerContentChange={() => runAndClose(onUndoSelectedLayerContentChange)}
                onRedoSelectedLayerContentChange={() => runAndClose(onRedoSelectedLayerContentChange)}
                onFlipHorizontalSelectedLayer={() => runAndClose(onFlipHorizontalSelectedLayer)}
                onFlipVerticalSelectedLayer={() => runAndClose(onFlipVerticalSelectedLayer)}
                onWriteSelectedLayerToBaseImage={() => runAndClose(onWriteSelectedLayerToBaseImage)}
                onMergeSelectedLayer={() => runAndClose(onMergeSelectedLayer)}
                onMergeLayerWithPrevious={() => runAndClose(onMergeLayerWithPrevious)}
                onRotateSelectedLayer={(degrees:number) => {
                    onRotateSelectedLayer?.(degrees);
                    closeContextMenu();
                }}
                canUndo={canUndoLayer}
                canRedo={canRedoLayer}
                selectedLayer={selectedLayer}
                onHideSelectedLayer={onHideSelectedLayer}
                onChangeLayerToBack={() => runAndClose(onChangeLayerToBack)}
                onChangeLayerToFront={() => runAndClose(onChangeLayerToFront)}
                />
            )}

            {layerareaSelection && (
                <ContextMenuAreaSelection
                onDeleteSelectedAreaFromLayer={() => runAndClose(onDeleteSelectedAreaFromLayer)}
                onDuplicateSelectedAreaFromLayer={() => runAndClose(onDuplicateSelectedAreaFromLayer)}
                onExtractSelectedAreaToNewLayer={() => runAndClose(onExtractSelectedAreaToNewLayer)}
                />
            )}

            <ContextMenuAiMode
                onRemoveBackground={(target) => runAndClose(() => onRemoveBackground?.(target))}
                onRemoveObject={(target) => runAndClose(() => onRemoveObject?.(target))}
                onExtractObject={(target) => runAndClose(() => onExtractObject?.(target))}
                onConvertToImageLayer={(target) => runAndClose(() => onConvertToImageLayer?.(target))}
                onUpscale={(target) => runAndClose(() => onUpscale?.(target))}
                onCreateVariations={(target) => runAndClose(() => onCreateVariations?.(target))}
                onExtractPose={(target) => runAndClose(() => onExtractPose?.(target))}
                onSpellCorrection={(target) => runAndClose(() => onSpellCorrection?.(target))}
                baseImage={baseImage}
                selectedLayer={selectedLayer}
                layerareaSelection={layerareaSelection}
                selectedLayerIDs={selectedLayerIDs}
                workOnBaseImage={workOnBaseImage}
            />
            </>
        </EffectThemeContainer>
        </div>
    );

    return createPortal(menuElement, document.body);
};

const ContextMenuBaseImage = ({
  rotateList,
  allLayer=[],
  selectedLayerIDs=[],
  onFlipHorizontalBaseImage,
  onFlipVerticalBaseImage,
  onRedoBaseImage,
  onResetBaseImage,
  onRotateBaseImage,
  onUndoBaseImage,
  onWriteAllLayerToBaseImage,
  onWriteSelectedLayerToBaseImage,
  onZoomIn,
  onZoomOut,
  onCropBaseImage,
  addBaseImageasLayerAndSetImageAsTransparentBackground
}: ContextMenuBaseImageProps) => {
    const { t } = useTranslation('imageStudioToolbar');
  return (
    <ContextMenuSubmenu 
        label={<span>
            <FontAwesomeIcon
                icon={faImage}
                className="me-2"
            />
            {t('menu.baseImage')}
        </span>}
    >
        <ContextMenuItem onClick={onUndoBaseImage}>
            <span>
                <FontAwesomeIcon
                className="me-2"
                size="sm"
                icon={faArrowRotateBack}
                />
                {t('basefn.undo')}
            </span>
        </ContextMenuItem>
        <ContextMenuItem onClick={onRedoBaseImage}>
            <span>
                <FontAwesomeIcon
                className="me-2"
                size="sm"
                icon={faArrowRotateForward}
                />
                {t('basefn.redo')}
            </span>
        </ContextMenuItem>
        <ContextMenuItem onClick={onResetBaseImage}>
            <span>
                <FontAwesomeIcon
                className="me-2"
                size="sm"
                icon={faPowerOff}
                />
                {t('menu.reset')}
            </span>
        </ContextMenuItem>
        <ContextMenuDivider />

        <ContextMenuItem onClick={onFlipHorizontalBaseImage}>
            <span>
                <FontAwesomeIcon
                    className="me-2"
                    icon={faArrowRightArrowLeft} 
                    size="sm"
                />
                {t('menu.flipH')}
            </span>
        </ContextMenuItem>
        <ContextMenuItem onClick={onFlipVerticalBaseImage}>
            <span>
                <FontAwesomeIcon
                    className="me-2"
                    icon={faArrowRightArrowLeft} 
                    rotation={90}
                    size="sm"
                />
                {t('menu.flipV')}
            </span>
        </ContextMenuItem>
        <ContextMenuItem onClick={onCropBaseImage}>
            <span>
                <FontAwesomeIcon
                    className="me-2"
                    icon={faCropSimple}
                    size="sm"
                />
                {t('menu.crop')}
            </span>
        </ContextMenuItem>
        <ContextMenuSubmenu
            label={<span>
                <FontAwesomeIcon
                className="me-2"
                size="sm"
                icon={faArrowsRotate}
                />
                {t('menu.rotate')}
            </span>}
        >
            {rotateList.map((degrees) => (
            <ContextMenuItem key={degrees} onClick={() => {
                onRotateBaseImage?.(degrees);
                }}>
                {degrees}°
            </ContextMenuItem>
            ))}
        </ContextMenuSubmenu>
        <ContextMenuDivider />

        <ContextMenuItem onClick={onZoomIn}>
            <span>
                <FontAwesomeIcon
                    className="me-2"
                    icon={faMagnifyingGlassPlus}
                    size="sm"
                />
                {t('menu.zoomIn')}
            </span>
        </ContextMenuItem>
        <ContextMenuItem onClick={onZoomOut}>
            <span>
                <FontAwesomeIcon
                    className="me-2"
                    icon={faMagnifyingGlassMinus}
                    size="sm"
                />
                {t('menu.zoomOut')}
            </span>
        </ContextMenuItem>

        <ContextMenuDivider />
        <ContextMenuItem onClick={addBaseImageasLayerAndSetImageAsTransparentBackground}>
            <span>
                <FontAwesomeIcon
                className="me-2"
                size="sm"
                icon={faPlus}
                />
                {t('menu.addBaseAsLayer')}
            </span>
        </ContextMenuItem>
        {
            selectedLayerIDs && selectedLayerIDs.length > 0
            ?   <ContextMenuItem onClick={onWriteSelectedLayerToBaseImage}>
                    <span>
                        <FontAwesomeIcon
                        className="me-2"
                        size="sm"
                        icon={faPenToSquare}
                        />
                        {t('menu.writeSelectedToBase')}
                    </span>
                </ContextMenuItem>
            :   <></>
        }
        {
            allLayer.length > 0
            ?   <ContextMenuItem onClick={onWriteAllLayerToBaseImage}>
                    <span>
                        <FontAwesomeIcon
                        className="me-2"
                        size="sm"
                        icon={faObjectGroup}
                        />
                        {t('menu.writeAllToBase')}
                    </span>
                </ContextMenuItem>
            :   <></>
        }
    </ContextMenuSubmenu>
  );
};

const ContextMenuLayer = ({
  rotateList=[],
  selectedLayerIDs=[],
  canUndo=false,
  canRedo=false,
  selectedLayer,
  onDeleteSelectedLayer,
  onDuplicateSelectedLayer,
  onRotateSelectedLayer,
  onCropSelectedLayerSave,
  onUndoSelectedLayerContentChange,
  onRedoSelectedLayerContentChange,
  onFlipHorizontalSelectedLayer,
  onFlipVerticalSelectedLayer,
  onWriteSelectedLayerToBaseImage,
  onMergeSelectedLayer,
  onMergeLayerWithPrevious,
  onHideSelectedLayer,
  onChangeLayerToBack,
  onChangeLayerToFront
}: ContextMenuLayerProps) => {
    const { t } = useTranslation('imageStudioToolbar');
  return (
    <ContextMenuSubmenu 
        label={<span>
            <FontAwesomeIcon
                icon={faLayerGroup}
                className="me-2"
            />
            {t('menu.selectedLayer')}
        </span>}
    >
        {
            canUndo && selectedLayer
            ?   <ContextMenuItem onClick={onUndoSelectedLayerContentChange}>
                    <span>
                        <FontAwesomeIcon
                            icon={faArrowRotateBack}
                            className="me-2"
                            size="sm"
                        />
                        {t('layerfn.undo')}
                    </span>
                </ContextMenuItem>
            : <></>
        }
        {
            canRedo && selectedLayer
            ?   <ContextMenuItem onClick={onRedoSelectedLayerContentChange}>
                    <span>
                        <FontAwesomeIcon
                            icon={faArrowRotateForward}
                            className="me-2"
                            size="sm"
                        />
                        {t('layerfn.redo')}
                    </span>
                </ContextMenuItem>
            :   <></>
        }
        {
            canRedo || canRedo
            ? <ContextMenuDivider />
            : <></>
        }
        <ContextMenuItem onClick={onChangeLayerToBack}>
            <span>
                <FontAwesomeIcon
                    className="me-2"
                    icon={faBackwardStep} 
                    size="sm"
                />
                {t('menu.moveBack')}
            </span>
        </ContextMenuItem>
        <ContextMenuItem onClick={onChangeLayerToFront}>
            <span>
                <FontAwesomeIcon
                    className="me-2"
                    icon={faForwardStep} 
                    size="sm"
                />
                {t('menu.moveFront')}
            </span>
        </ContextMenuItem>
        <ContextMenuDivider />

        {
            selectedLayer
            ?   <>
                    <ContextMenuItem onClick={onDuplicateSelectedLayer}>
                        <span>
                            <FontAwesomeIcon
                                icon={faCopy}
                                className="me-2"
                                size="sm"
                            />
                            {t('menu.duplicate')}
                        </span>
                    </ContextMenuItem>
                    <ContextMenuItem onClick={onDeleteSelectedLayer}>
                        <span>
                            <FontAwesomeIcon
                                icon={faTrashCan}
                                className="me-2"
                                size="sm"
                            />
                            {t('menu.delete')}
                        </span>
                    </ContextMenuItem>
                    {   selectedLayer.type !== 'text'
                        ?   <ContextMenuItem onClick={onCropSelectedLayerSave}>
                                <span>
                                    <FontAwesomeIcon
                                        icon={faCropSimple}
                                        className="me-2"
                                        size="sm"
                                    />
                                    {t('menu.crop')}
                                </span>
                            </ContextMenuItem>
                        :   <></>

                    }
                    <ContextMenuItem onClick={onFlipHorizontalSelectedLayer}>
                        <span>
                            <FontAwesomeIcon
                                className="me-2"
                                icon={faArrowRightArrowLeft} 
                                size="sm"
                            />
                            {t('menu.flipH')}
                        </span>
                    </ContextMenuItem>
                    <ContextMenuItem onClick={onFlipVerticalSelectedLayer}>
                        <span>
                            <FontAwesomeIcon
                                className="me-2"
                                icon={faArrowRightArrowLeft} 
                                rotation={90}
                                size="sm"
                            />
                            {t('menu.flipV')}
                        </span>
                    </ContextMenuItem>
                    <ContextMenuSubmenu 
                        label={<span>
                            <FontAwesomeIcon
                            className="me-2"
                            size="sm"
                            icon={faArrowRotateBack}
                            />
                            {t('menu.rotate')}
                        </span>}
                    >
                        {rotateList.map((degrees: number) => (
                        <ContextMenuItem key={degrees} onClick={() => {
                            onRotateSelectedLayer?.(degrees);
                        }}>
                            {degrees}°
                        </ContextMenuItem>
                        ))}
                    </ContextMenuSubmenu>
                    <ContextMenuDivider />
                </>
            :   <></>
        }
        {
            selectedLayerIDs.length > 1
            ?   <ContextMenuItem onClick={onMergeSelectedLayer}>
                    <span>
                        <FontAwesomeIcon
                            className="me-2"
                            icon={faHexagonNodes} 
                            size="sm"
                        />
                        {t('menu.mergeSelected')}
                    </span>
                </ContextMenuItem>
            : <></>
        }
        {
            selectedLayer && Number(selectedLayer.orderNumber ?? 0) > 1
            ?   <ContextMenuItem onClick={onMergeLayerWithPrevious}>
                    <span>
                        <FontAwesomeIcon
                            className="me-2"
                            icon={faDiagramProject} 
                            size="sm"
                        />
                        {t('menu.mergePrev')}
                    </span>
                    
                </ContextMenuItem>
            : <></>
        }
        {
            selectedLayer || selectedLayerIDs.length > 0
            ?   <>
                    <ContextMenuItem onClick={onWriteSelectedLayerToBaseImage}>
                        <span>
                            <FontAwesomeIcon
                                className="me-2"
                                icon={faPenToSquare} 
                                size="sm"
                            />
                            {t('menu.writeLayerToBase')}
                        </span>
                    </ContextMenuItem>
                    <ContextMenuItem onClick={onHideSelectedLayer}>
                    <span>
                        <FontAwesomeIcon
                            className="me-2"
                            icon={faEyeSlash} 
                            size="sm"
                        />
                        {t('menu.hideLayer')}
                    </span>
                </ContextMenuItem>
                </>
            :   <></>
        }
    </ContextMenuSubmenu>
  );
};

export const ContextMenuEmptyBase = ({
  wrapperRef,
  contextMenu,
  uploadBaseImage,
  setContextMenu,
}: ContextMenuEmptyBaseProps) => {
    const { t } = useTranslation('imageStudioToolbar');
    const menuRef = useRef<HTMLDivElement | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const [menuPlacement, setMenuPlacement] = useState({
        x: contextMenu.x,
        y: contextMenu.y,
        openLeft: false,
        openUp: false
    });

    const closeContextMenu = () => {
        setContextMenu((prev) => ({ ...prev, visible: false }));
    };

    const runAndClose = (callback?: () => void) => {
        callback?.();
        closeContextMenu();
    };

    useEffect(() => {
        const onMouseDown = (e: MouseEvent) => {
        if (menuRef.current?.contains(e.target as Node)) return;
        closeContextMenu();
        };

        const onKeyDown = (e: KeyboardEvent) => {
        if (e.key === "Escape") closeContextMenu();
        };

        window.addEventListener("mousedown", onMouseDown);
        window.addEventListener("keydown", onKeyDown);

        return () => {
        window.removeEventListener("mousedown", onMouseDown);
        window.removeEventListener("keydown", onKeyDown);
        };
    }, []);

    useEffect(() => {
        const el = wrapperRef.current;
        if (!el) return;

        const onContextMenu = (e: MouseEvent) => {
        e.preventDefault();

            const rect = el.getBoundingClientRect();

            setContextMenu({
                visible: true,
                x: e.clientX,
                y: e.clientY
            });
        };

        el.addEventListener("contextmenu", onContextMenu);

        return () => {
        el.removeEventListener("contextmenu", onContextMenu);
        };
    }, [wrapperRef]);
    useLayoutEffect(() => {
        if (!contextMenu.visible) return;

        const menu = menuRef.current;
        if (!menu) return;

        const padding = 8;
        const submenuWidth = 240;

        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        const menuWidth = menu.offsetWidth;
        const menuHeight = menu.offsetHeight;

        const openLeft =
            contextMenu.x + menuWidth + submenuWidth + padding > viewportWidth;

        const openUp =
            contextMenu.y + menuHeight + padding > viewportHeight;

        const nextX = Math.max(
            padding,
            Math.min(contextMenu.x, viewportWidth - menuWidth - padding)
        );

        const nextY = Math.max(
            padding,
            Math.min(contextMenu.y, viewportHeight - menuHeight - padding)
        );

        setMenuPlacement({
            x: nextX,
            y: nextY,
            openLeft,
            openUp
        });
        }, [contextMenu.visible, contextMenu.x, contextMenu.y]);

    if (!contextMenu.visible) return null;

    const menuElement = (
        <div
            ref={menuRef}
            className={[
                "context-menu-position",
                menuPlacement.openLeft ? "context-menu-open-left" : "",
                menuPlacement.openUp ? "context-menu-open-up" : ""
            ].join(" ")}
            style={{
                left: menuPlacement.x,
                top: menuPlacement.y
            }}
            onContextMenu={(e) => e.preventDefault()}
            onMouseDown={(e) => e.stopPropagation()}
        >
        <EffectThemeContainer containerClass="context-menu-container">
            <>
                <ContextMenuItem
                    onClick={() => {
                        fileInputRef.current?.click();
                    }}
                >
                    <span>
                        <FontAwesomeIcon
                            icon={faImage}
                            className="me-2"
                            size="sm"
                        />
                        {t('menu.uploadImage')}
                    </span>

                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        hidden
                        onClick={(e) => {
                            e.stopPropagation();
                        }}
                        onChange={(e) => {
                            const file = e.currentTarget.files?.[0];
                            if (!file) return;

                            runAndClose(() => uploadBaseImage(file));

                            e.currentTarget.value = "";
                        }}
                    />
                </ContextMenuItem>
            </>
        </EffectThemeContainer>
        </div>
    );

    return createPortal(menuElement, document.body);
};

const ContextMenuAreaSelection = ({
  onDeleteSelectedAreaFromLayer,
  onDuplicateSelectedAreaFromLayer,
  onExtractSelectedAreaToNewLayer,
}: ContextMenuAreaSelectionProps) => {
    const { t } = useTranslation('imageStudioToolbar');
  return (
    <ContextMenuSubmenu 
        label={<span>
            <FontAwesomeIcon
                icon={faCircle}
                className="me-2"
            />
            {t('menu.areaSelection')}
        </span>}
    >

      <ContextMenuItem onClick={onDeleteSelectedAreaFromLayer}>
        <span>
            <FontAwesomeIcon
                className="me-2"
                icon={faTrashCan} 
                size="sm"
            />
            {t('menu.delete')}
        </span>
      </ContextMenuItem>
      <ContextMenuItem onClick={onDuplicateSelectedAreaFromLayer}>
        <span>
            <FontAwesomeIcon
                className="me-2"
                icon={faCopy} 
                size="sm"
            />
            {t('menu.copy')}
        </span>
      </ContextMenuItem>
      <ContextMenuItem onClick={onExtractSelectedAreaToNewLayer}>
        <span>
            <FontAwesomeIcon
                className="me-2"
                icon={faCrop} 
                size="sm"
            />
            {t('menu.extract')}
        </span>
      </ContextMenuItem>
    </ContextMenuSubmenu>
  );
};

const ContextMenuAiMode = ({
  onRemoveBackground,
  onRemoveObject,
  onExtractObject,
  onConvertToImageLayer,
  onUpscale,
  onCreateVariations,
  onExtractPose,
  onSpellCorrection,
  selectedLayer,
  selectedLayerIDs=[],
  baseImage,
  workOnBaseImage,
  layerareaSelection
}: ContextMenuAiModeProps) => {
    const { t } = useTranslation('imageStudioToolbar');
  return (
    <ContextMenuSubmenu 
        label={<span>
            <FontAwesomeIcon
                icon={faWandMagic}
                className="me-2"
            />
            {t('menu.aiMode')}
            <AILogo className="ms-2"/>
        </span>}
    >
    {
        baseImage && (!selectedLayer || workOnBaseImage)
        ?   <ContextMenuSubmenu
                    label={<span>
                    <FontAwesomeIcon
                        icon={faImage}
                        className="me-2"
                    />
                    {t('menu.baseImage')}
                </span>}
            >
                <ContextMenuItem onClick={() => onRemoveBackground?.('base')}>
                    <span>
                        <FontAwesomeIcon
                            className="me-2"
                            icon={faX} 
                            size="sm"
                        />
                        {t('menu.removeBackground')}
                    </span>
                </ContextMenuItem>
                <ContextMenuItem onClick={() => onUpscale?.('base')}>
                    <span>
                        <FontAwesomeIcon
                            className="me-2"
                            icon={faUpRightAndDownLeftFromCenter} 
                            size="sm"
                        />
                        {t('menu.upscaleTypo')}
                    </span>
                </ContextMenuItem>
                <ContextMenuItem onClick={() => onCreateVariations?.('base')}>
                    <span>
                        <FontAwesomeIcon
                            className="me-2"
                            icon={faPlaneUp} 
                            size="sm"
                        />
                        {t('menu.generateVariations')}
                    </span>
                </ContextMenuItem>
                {isFeatureEnabled('imageExtractPose') &&
                    <ContextMenuItem onClick={() => onExtractPose?.('base')}>
                        <span>
                            <FontAwesomeIcon
                                className="me-2"
                                icon={faPerson}
                                size="sm"
                            />
                            {t('menu.extractPose')}<StageBadge feature="imageExtractPose"/>
                        </span>
                    </ContextMenuItem>
                }
            </ContextMenuSubmenu>
        :   <></>
    }
    {
        (selectedLayer && (selectedLayer.type === 'image' || selectedLayer.type === 'ai-variation')) || selectedLayerIDs.length > 1
        ?   <ContextMenuSubmenu
                    label={<span>
                    <FontAwesomeIcon
                        icon={faObjectGroup}
                        className="me-2"
                    />
                    {t('menu.imageLayer')}
                </span>}
            >
                <ContextMenuItem onClick={() => onRemoveBackground?.('layer')}>
                    <span>
                        <FontAwesomeIcon
                            className="me-2"
                            icon={faX} 
                            size="sm"
                        />
                        {t('menu.removeBackground')}
                    </span>
                </ContextMenuItem>
                <ContextMenuItem onClick={() => onUpscale?.('layer')}>
                    <span>
                        <FontAwesomeIcon
                            className="me-2"
                            icon={faUpRightAndDownLeftFromCenter} 
                            size="sm"
                        />
                        {t('menu.upscaleTypo')}
                    </span>
                </ContextMenuItem>
                <ContextMenuItem onClick={() => onCreateVariations?.('layer')}>
                    <span>
                        <FontAwesomeIcon
                            className="me-2"
                            icon={faPlaneUp} 
                            size="sm"
                        />
                        {t('menu.generateVariations')}
                    </span>
                </ContextMenuItem>
                {isFeatureEnabled('imageExtractPose') &&
                    <ContextMenuItem onClick={() => onExtractPose?.('layer')}>
                        <span>
                            <FontAwesomeIcon
                                className="me-2"
                                icon={faPerson}
                                size="sm"
                            />
                            {t('menu.extractPose')}<StageBadge feature="imageExtractPose"/>
                        </span>
                    </ContextMenuItem>
                }
            </ContextMenuSubmenu>
        :   <></>
    }
    {   selectedLayer && selectedLayer.type === 'text'
        ?   <ContextMenuSubmenu
                    label={<span>
                    <FontAwesomeIcon
                        icon={faT}
                        className="me-2"
                    />
                    {t('menu.textLayer')}
                </span>}
            >
                <ContextMenuItem onClick={() => onConvertToImageLayer?.('layer')}>
                    <span>
                        <FontAwesomeIcon
                            className="me-2"
                            icon={faImage} 
                            size="sm"
                        />
                        {t('menu.convertToImage')}
                    </span>
                </ContextMenuItem>
                <ContextMenuItem onClick={() => onSpellCorrection?.('layer')}>
                    <span>
                        <FontAwesomeIcon
                            className="me-2"
                            icon={faComment} 
                            size="sm"
                        />
                        {t('menu.spellCorrection')}
                    </span>
                </ContextMenuItem>
            </ContextMenuSubmenu>
        : <></>
    }
        
    {
        selectedLayer && selectedLayer.type === 'drawings'
        ?   <ContextMenuSubmenu
                label={<span>
                    <FontAwesomeIcon
                        icon={faPaintBrush}
                        className="me-2"
                    />
                    {t('menu.drawingLayer')}
                </span>}
            >
                <ContextMenuItem onClick={() => onConvertToImageLayer?.('layer')}>
                    <span>
                        <FontAwesomeIcon
                            className="me-2"
                            icon={faImage} 
                            size="sm"
                        />
                        {t('menu.convertToImage')}
                    </span>
                </ContextMenuItem>
            </ContextMenuSubmenu>
        :   <></>
    }
      {
        layerareaSelection
        ?   <ContextMenuSubmenu
                label={<span>
                    <FontAwesomeIcon
                        icon={faPaintBrush}
                        className="me-2"
                    />
                    {t('menu.selectedArea')}
                </span>}
            >
                <ContextMenuItem onClick={() => onRemoveObject?.(selectedLayer ? 'base' : 'layer')}>
                    <span>
                        <FontAwesomeIcon
                            className="me-2"
                            icon={faTrashCan} 
                            size="sm"
                        />
                        {t('menu.removeObject')}
                    </span>
                </ContextMenuItem>
                <ContextMenuItem onClick={() => onExtractObject?.(selectedLayer ? 'base' : 'layer')}>
                    <span>
                        <FontAwesomeIcon
                            className="me-2"
                            icon={faShapes} 
                            size="sm"
                        />
                        {t('menu.extractObject')}
                    </span>
                </ContextMenuItem>
            </ContextMenuSubmenu>
        :   <></>
      }
    </ContextMenuSubmenu>
  );
};

const ContextMenuItem = ({
  children,
  onClick,
  disabled,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
}) => {
  return (
    <EffectThemeDropdown.Item className="context-menu-item p-0 m-0 ps-2" onClick={onClick} disabled={disabled}>
      {children}
    </EffectThemeDropdown.Item>
  );
};

const ContextMenuDivider = () => {
  return <div className="context-menu-divider" />;
};

const ContextMenuSubmenu = ({
  label,
  children,
  style
}: {
  label: string | JSX.Element;
  children: React.ReactNode;
  style?: CSSProperties;
}) => {
  const submenuRef = useRef<HTMLDivElement | null>(null);
  const submenuListRef = useRef<HTMLDivElement | null>(null);

  const [submenuTop, setSubmenuTop] = useState(-6);

  const updateSubmenuPosition = () => {
    const submenu = submenuRef.current;
    const submenuList = submenuListRef.current;

    if (!submenu || !submenuList) return;

    const padding = 8;
    const submenuRect = submenu.getBoundingClientRect();

    // kurz sichtbar machen, damit offsetHeight korrekt ist
    submenuList.style.visibility = "hidden";
    submenuList.style.display = "block";

    const submenuHeight = submenuList.offsetHeight;

    submenuList.style.display = "";
    submenuList.style.visibility = "";

    const preferredTop = -6;
    const submenuBottom = submenuRect.top + preferredTop + submenuHeight;
    const overflowBottom = submenuBottom - (window.innerHeight - padding);

    let nextTop = preferredTop;

    if (overflowBottom > 0) {
      nextTop = preferredTop - overflowBottom;
    }

    const submenuTopOnScreen = submenuRect.top + nextTop;

    if (submenuTopOnScreen < padding) {
      nextTop += padding - submenuTopOnScreen;
    }

    setSubmenuTop(nextTop);
  };

  return (
    <div
      ref={submenuRef}
      className="context-menu-submenu"
      style={{zIndex: 2100 }}
      onMouseEnter={updateSubmenuPosition}
      onFocus={updateSubmenuPosition}
    >
      <EffectThemeDropdown.Item className="context-menu-item context-menu-submenu-trigger p-1 ps-2">
        <span>{label}</span>
        <span className="context-menu-arrow">›</span>
      </EffectThemeDropdown.Item>

      <div
        ref={submenuListRef}
        className="context-menu-submenu-list"
        style={
          {
            "--context-submenu-top": `${submenuTop}px`,
          } as CSSProperties
        }
      >
        <EffectThemeContainer containerClass="context-menu-submenu-container" style={{width:'fit-content', minWidth:250}}>
          <>{children}</>
        </EffectThemeContainer>
      </div>
    </div>
  );
};

export const ContextMenuLayerSidebar = ({
    wrapperRef,
    contextMenu,
    setContextMenu,
    layerOrderNumber,
    layerID,
    layerType,
    allLayer,
    //base image functions
    baseImage,
    rotateList=[],
    addBaseImageasLayerAndSetImageAsTransparentBackground,
    onResetBaseImage,
    onUndoBaseImage,
    onRedoBaseImage,
    onRotateBaseImage,
    onFlipVerticalBaseImage,
    onFlipHorizontalBaseImage,
    onWriteAllLayerToBaseImage,
    onWriteSelectedLayerToBaseImage,
    onZoomIn,
    onZoomOut,
    onCropBaseImage,
    //layer
    selectedLayerIDs,
    onDeleteSelectedLayer,
    onDuplicateSelectedLayer,
    onRotateSelectedLayer,
    onCropSelectedLayerSave,
    onFlipHorizontalSelectedLayer,
    onFlipVerticalSelectedLayer,
    onMergeSelectedLayer,
    onMergeLayerWithPrevious,
    onHideSelectedLayer,
    onChangeLayerToBack,
    onChangeLayerToFront
}: ContextMenuLayerSidebarProps) => {
    const { t } = useTranslation('imageStudioToolbar');
    const menuRef = useRef<HTMLDivElement | null>(null);

    const [menuPlacement, setMenuPlacement] = useState({
        x: contextMenu.x,
        y: contextMenu.y,
        openLeft: false,
        openUp: false
    });

    const closeContextMenu = () => {
        setContextMenu((prev) => ({ ...prev, visible: false }));
    };

    const runAndClose = (callback?: () => void) => {
        callback?.();
        closeContextMenu();
    };

    useEffect(() => {
        const onMouseDown = (e: MouseEvent) => {
        if (menuRef.current?.contains(e.target as Node)) return;
        closeContextMenu();
        };

        const onKeyDown = (e: KeyboardEvent) => {
        if (e.key === "Escape") closeContextMenu();
        };

        window.addEventListener("mousedown", onMouseDown);
        window.addEventListener("keydown", onKeyDown);

        return () => {
        window.removeEventListener("mousedown", onMouseDown);
        window.removeEventListener("keydown", onKeyDown);
        };
    }, []);

    useLayoutEffect(() => {
        if (!contextMenu.visible) return;

        const menu = menuRef.current;
        if (!menu) return;

        const padding = 8;
        const submenuWidth = 240;

        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        const menuWidth = menu.offsetWidth;
        const menuHeight = menu.offsetHeight;

        const openLeft =
            contextMenu.x + menuWidth + submenuWidth + padding > viewportWidth;

        const openUp =
            contextMenu.y + menuHeight + padding > viewportHeight;

        const nextX = Math.max(
            padding,
            Math.min(contextMenu.x, viewportWidth - menuWidth - padding)
        );

        const nextY = Math.max(
            padding,
            Math.min(contextMenu.y, viewportHeight - menuHeight - padding)
        );

        setMenuPlacement({
            x: nextX,
            y: nextY,
            openLeft,
            openUp
        });
    }, [contextMenu.visible, contextMenu.x, contextMenu.y]);

    if (!contextMenu.visible) return null;

    const currentLayer =
        layerID != null
            ? allLayer.find(layer => layer.layerID === layerID) ?? null
            : null;

    const currentLayerHidden = currentLayer?.hidden ?? false;

    const menuElement = (
        <div
            ref={menuRef}
            className={[
                "context-menu-position",
                menuPlacement.openLeft ? "context-menu-open-left" : "",
                menuPlacement.openUp ? "context-menu-open-up" : ""
            ].join(" ")}
            style={{
                left: menuPlacement.x,
                top: menuPlacement.y
            }}
            onContextMenu={(e) => e.preventDefault()}
            onMouseDown={(e) => e.stopPropagation()}
        >
        <EffectThemeContainer containerClass="context-menu-container">
            <>

            {
                layerOrderNumber != null && layerID != null && layerType
                ?   <>
                        <span className="fw-bold"> Layer (# {layerOrderNumber})</span>
                        <ContextMenuItem onClick={onChangeLayerToBack}>
                            <span>
                                <FontAwesomeIcon
                                    className="me-2"
                                    icon={faBackwardStep} 
                                    size="sm"
                                />
                                {t('menu.moveBack')}
                            </span>
                        </ContextMenuItem>
                        <ContextMenuItem onClick={onChangeLayerToFront}>
                            <span>
                                <FontAwesomeIcon
                                    className="me-2"
                                    icon={faForwardStep} 
                                    size="sm"
                                />
                                {t('menu.moveFront')}
                            </span>
                        </ContextMenuItem>
                        <ContextMenuDivider />
                        <ContextMenuItem onClick={() => runAndClose(() => onDuplicateSelectedLayer?.(layerID))}>
                            <span>
                                <FontAwesomeIcon
                                    icon={faCopy}
                                    className="me-2"
                                    size="sm"
                                />
                                {t('menu.duplicate')}
                            </span>
                        </ContextMenuItem>
                        <ContextMenuItem onClick={() => runAndClose(() => onDeleteSelectedLayer?.(layerID))}>
                            <span>
                                <FontAwesomeIcon
                                    icon={faTrashCan}
                                    className="me-2"
                                    size="sm"
                                />
                                {t('menu.delete')}
                            </span>
                        </ContextMenuItem>

                        <ContextMenuItem
                            onClick={() =>
                                runAndClose(() =>
                                    layerID != null
                                        ? onHideSelectedLayer?.(layerID, !currentLayerHidden)
                                        : undefined
                                )
                            }
                        >
                            <span>
                                <FontAwesomeIcon
                                    className="me-2"
                                    icon={currentLayerHidden ? faEye : faEyeSlash}
                                    size="sm"
                                />
                                {currentLayerHidden ? t('layerfn.unhide') : t('layerfn.hide')}
                            </span>
                        </ContextMenuItem>

                        <ContextMenuItem onClick={() => runAndClose(() => onMergeLayerWithPrevious?.(layerID))}>
                            <span>
                                <FontAwesomeIcon
                                    className="me-2"
                                    icon={faDiagramProject} 
                                    size="sm"
                                />
                                {t('menu.mergePrev')}
                            </span>
                        </ContextMenuItem>
                        <ContextMenuItem onClick={() => runAndClose(() => onWriteSelectedLayerToBaseImage?.([layerID]))}>
                            <span>
                                <FontAwesomeIcon
                                    className="me-2"
                                    icon={faPenToSquare} 
                                    size="sm"
                                />
                                {t('menu.writeLayerToBase')}
                            </span>
                        </ContextMenuItem>
                        <ContextMenuItem onClick={() => runAndClose(() => onFlipHorizontalSelectedLayer?.(layerID))}>
                            <span>
                                <FontAwesomeIcon
                                    className="me-2"
                                    icon={faArrowRightArrowLeft} 
                                    size="sm"
                                />
                                {t('menu.flipH')}
                            </span>
                        </ContextMenuItem>
                        <ContextMenuItem onClick={() => runAndClose(() => onFlipVerticalSelectedLayer?.(layerID))}>
                            <span>
                                <FontAwesomeIcon
                                    className="me-2"
                                    icon={faArrowRightArrowLeft} 
                                    rotation={90}
                                    size="sm"
                                />
                                {t('menu.flipV')}
                            </span>
                        </ContextMenuItem>
                        <ContextMenuSubmenu 
                            label={<span>
                                <FontAwesomeIcon
                                className="me-2"
                                size="sm"
                                icon={faArrowRotateBack}
                                />
                                {t('menu.rotate')}
                            </span>}
                        >
                            {rotateList.map((degrees: number) => (
                            <ContextMenuItem key={degrees} onClick={() => runAndClose(() => {
                                console.log('Rotate layer: ', layerID)
                                onRotateSelectedLayer?.(degrees,layerID);
                            })}>
                                {degrees}°
                            </ContextMenuItem>
                            ))}
                        </ContextMenuSubmenu>
                        {   layerType !== 'text'
                            ?   <ContextMenuItem onClick={() => runAndClose(() => onCropSelectedLayerSave?.(layerID))}>
                                    <span>
                                        <FontAwesomeIcon
                                            icon={faCropSimple}
                                            className="me-2"
                                            size="sm"
                                        />
                                        {t('menu.crop')}
                                    </span>
                                </ContextMenuItem>
                            :   <></>
                        }
                        
                        {
                            selectedLayerIDs && selectedLayerIDs.length > 1
                            ?   <>
                                    <ContextMenuDivider />
                                    <span className="fw-bold">{t('menu.selectedLayers')}</span>
                                    <ContextMenuItem onClick={() => runAndClose(onMergeSelectedLayer)}>
                                        <span>
                                            <FontAwesomeIcon
                                                className="me-2"
                                                icon={faHexagonNodes} 
                                                size="sm"
                                            />
                                            {t('menu.mergeSelectedLayers')}
                                        </span>
                                    </ContextMenuItem>
                                    <ContextMenuItem onClick={() => runAndClose(() => onWriteSelectedLayerToBaseImage?.(selectedLayerIDs))}>
                                        <span>
                                            <FontAwesomeIcon
                                                className="me-2"
                                                icon={faPenToSquare} 
                                                size="sm"
                                            />
                                            {t('menu.writeLayersToBase')}
                                        </span>
                                    </ContextMenuItem>
                                </>
                            :   <></>
                        }
                        {
                            allLayer.length > 0
                            ?   <>
                                    <ContextMenuDivider />
                                    <span className="fw-bold">{t('menu.allLayers')}</span>
                                    <ContextMenuItem onClick={() => runAndClose(onWriteAllLayerToBaseImage)}>
                                        <span>
                                            <FontAwesomeIcon
                                                className="me-2"
                                                icon={faPenToSquare} 
                                                size="sm"
                                            />
                                            {t('menu.writeAllLayersToBase')}
                                        </span>
                                    </ContextMenuItem>
                                </>
                            :   <></>
                        }
                    </>
                :   <></>
            }
            {
                baseImage && layerID == null
                ?   <>
                        <span className="fw-bold">{t('menu.baseImage')}</span>
                        <ContextMenuItem onClick={() => runAndClose(onUndoBaseImage)}>
                            <span>
                                <FontAwesomeIcon
                                className="me-2"
                                size="sm"
                                icon={faArrowRotateBack}
                                />
                                {t('basefn.undo')}
                            </span>
                        </ContextMenuItem>
                        <ContextMenuItem onClick={() => runAndClose(onRedoBaseImage)}>
                            <span>
                                <FontAwesomeIcon
                                className="me-2"
                                size="sm"
                                icon={faArrowRotateForward}
                                />
                                {t('basefn.redo')}
                            </span>
                        </ContextMenuItem>
                        <ContextMenuItem onClick={() => runAndClose(onResetBaseImage)}>
                            <span>
                                <FontAwesomeIcon
                                className="me-2"
                                size="sm"
                                icon={faPowerOff}
                                />
                                {t('menu.reset')}
                            </span>
                        </ContextMenuItem>
                        <ContextMenuDivider />

                        <ContextMenuItem onClick={() => runAndClose(onFlipHorizontalBaseImage)}>
                            <span>
                                <FontAwesomeIcon
                                    className="me-2"
                                    icon={faArrowRightArrowLeft} 
                                    size="sm"
                                />
                                {t('menu.flipH')}
                            </span>
                        </ContextMenuItem>
                        <ContextMenuItem onClick={() => runAndClose(onFlipVerticalBaseImage)}>
                            <span>
                                <FontAwesomeIcon
                                    className="me-2"
                                    icon={faArrowRightArrowLeft} 
                                    rotation={90}
                                    size="sm"
                                />
                                {t('menu.flipV')}
                            </span>
                        </ContextMenuItem>
                        
                        <ContextMenuSubmenu
                            label={<span>
                                <FontAwesomeIcon
                                className="me-2"
                                size="sm"
                                icon={faArrowsRotate}
                                />
                                {t('menu.rotate')}
                            </span>}
                        >
                            {rotateList.map((degrees) => (
                            <ContextMenuItem key={degrees} onClick={() => runAndClose(() => {
                                onRotateBaseImage?.(degrees,undefined);
                                })}>
                                {degrees}°
                            </ContextMenuItem>
                            ))}
                        </ContextMenuSubmenu>
                        <ContextMenuItem onClick={() => runAndClose(onCropBaseImage)}>
                            <span>
                                <FontAwesomeIcon
                                    className="me-2"
                                    icon={faCropSimple}
                                    size="sm"
                                />
                                {t('menu.crop')}
                            </span>
                        </ContextMenuItem>
                        <ContextMenuDivider />

                        <ContextMenuItem onClick={() => runAndClose(onZoomIn)}>
                            <span>
                                <FontAwesomeIcon
                                    className="me-2"
                                    icon={faMagnifyingGlassPlus}
                                    size="sm"
                                />
                                {t('menu.zoomIn')}
                            </span>
                        </ContextMenuItem>
                        <ContextMenuItem onClick={() => runAndClose(onZoomOut)}>
                            <span>
                                <FontAwesomeIcon
                                    className="me-2"
                                    icon={faMagnifyingGlassMinus}
                                    size="sm"
                                />
                                {t('menu.zoomOut')}
                            </span>
                        </ContextMenuItem>

                        <ContextMenuDivider />

                        <ContextMenuItem onClick={addBaseImageasLayerAndSetImageAsTransparentBackground}>
                            <span>
                                <FontAwesomeIcon
                                className="me-2"
                                size="sm"
                                icon={faPlus}
                                />
                                {t('menu.addBaseAsLayer')}
                            </span>
                        </ContextMenuItem>
                        {
                            selectedLayerIDs && selectedLayerIDs.length > 0
                            ?   <ContextMenuItem onClick={() => runAndClose(() => onWriteSelectedLayerToBaseImage?.(selectedLayerIDs))}>
                                    <span>
                                        <FontAwesomeIcon
                                        className="me-2"
                                        size="sm"
                                        icon={faPenToSquare}
                                        />
                                        {t('menu.writeSelectedToBase')}
                                    </span>
                                </ContextMenuItem>
                            :   <></>
                        }
                        {
                            allLayer.length > 0
                            ?   <ContextMenuItem onClick={() => runAndClose(onWriteAllLayerToBaseImage)}>
                                    <span>
                                        <FontAwesomeIcon
                                        className="me-2"
                                        size="sm"
                                        icon={faObjectGroup}
                                        />
                                        {t('menu.writeAllToBase')}
                                    </span>
                                </ContextMenuItem>
                            :   <></>
                        }
                    </>
                :   <></>
            }
                
            </>
        </EffectThemeContainer>
        </div>
    );

    return createPortal(menuElement, document.body);
};

export const ContextMenuLayerSidebarVariations = ({
    contextMenu,
    setContextMenu,
    wrapperRef,
    variationsIndex,
    variation,
    allVariations,
    layer,
    baseImage,
    onAddVariationAsLayer,
    onChangeVariationAsLayer,
    onReplaceBaseImage,
    onSetVariationOnImage,
    onDeleteVariation,
    onDownloadAllVariations
}:ContextMenuLayerSidebarVariationsProps)=> {
    const { t } = useTranslation('imageStudioToolbar');
    const menuRef = useRef<HTMLDivElement | null>(null);

    const [menuPlacement, setMenuPlacement] = useState({
        x: contextMenu.x,
        y: contextMenu.y,
        openLeft: false,
        openUp: false
    });

    const closeContextMenu = () => {
        setContextMenu((prev) => ({ ...prev, visible: false }));
    };

    const runAndClose = (callback?: () => void) => {
        callback?.();
        closeContextMenu();
    };

    useEffect(() => {
        const onMouseDown = (e: MouseEvent) => {
        if (menuRef.current?.contains(e.target as Node)) return;
        closeContextMenu();
        };

        const onKeyDown = (e: KeyboardEvent) => {
        if (e.key === "Escape") closeContextMenu();
        };

        window.addEventListener("mousedown", onMouseDown);
        window.addEventListener("keydown", onKeyDown);

        return () => {
        window.removeEventListener("mousedown", onMouseDown);
        window.removeEventListener("keydown", onKeyDown);
        };
    }, []);

    useLayoutEffect(() => {
        if (!contextMenu.visible) return;

        const menu = menuRef.current;
        if (!menu) return;

        const padding = 8;
        const submenuWidth = 240;

        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        const menuWidth = menu.offsetWidth;
        const menuHeight = menu.offsetHeight;

        const openLeft =
            contextMenu.x + menuWidth + submenuWidth + padding > viewportWidth;

        const openUp =
            contextMenu.y + menuHeight + padding > viewportHeight;

        const nextX = Math.max(
            padding,
            Math.min(contextMenu.x, viewportWidth - menuWidth - padding)
        );

        const nextY = Math.max(
            padding,
            Math.min(contextMenu.y, viewportHeight - menuHeight - padding)
        );

        setMenuPlacement({
            x: nextX,
            y: nextY,
            openLeft,
            openUp
        });
    }, [contextMenu.visible, contextMenu.x, contextMenu.y]);

    if (!contextMenu.visible) return null;

    const menuElement = (
        <div
            ref={menuRef}
            className={[
                "context-menu-position",
                menuPlacement.openLeft ? "context-menu-open-left" : "",
                menuPlacement.openUp ? "context-menu-open-up" : ""
            ].join(" ")}
            style={{
                left: menuPlacement.x,
                top: menuPlacement.y
            }}
            onContextMenu={(e) => e.preventDefault()}
            onMouseDown={(e) => e.stopPropagation()}
        >
        <EffectThemeContainer containerClass="context-menu-container">
            <>
                <span className="fw-bold"> {t('menu.variation', { n: variationsIndex })}</span>
                {
                    baseImage && !layer && variation
                    ?   <>
                            <ContextMenuItem onClick={() => runAndClose(() => onReplaceBaseImage?.(variation))}>
                                <span>
                                    <FontAwesomeIcon
                                        icon={faLayerGroup}
                                        className="me-2"
                                        size="sm"
                                    />
                                    {t('menu.replaceBaseImage')}
                                </span>
                            </ContextMenuItem>
                        </>
                    :   variation && (<>
                            <ContextMenuItem onClick={() => runAndClose(() => onSetVariationOnImage?.(variation,layer))}>
                                <span>
                                    <FontAwesomeIcon
                                        icon={faObjectGroup}
                                        className="me-2"
                                        size="sm"
                                    />
                                    {t('menu.writeVariationOnBase')}
                                </span>
                            </ContextMenuItem>
                            <ContextMenuItem onClick={() => runAndClose(() => onChangeVariationAsLayer?.(variation,layer))}>
                                <span>
                                    <FontAwesomeIcon
                                        icon={faLayerGroup}
                                        className="me-2"
                                        size="sm"
                                    />
                                    {t('menu.changeLayerWithVariation')}
                                </span>
                            </ContextMenuItem>
                        </>)
                }  
                {
                    variation && variationsIndex !== null
                    ?   <>
                            <ContextMenuItem onClick={() => runAndClose(() => onAddVariationAsLayer?.(variation,layer ?? null))}>
                                <span>
                                    <FontAwesomeIcon
                                        icon={faPlus}
                                        className="me-2"
                                        size="sm"
                                    />
                                    {t('menu.addVariationAsLayer')}
                                </span>
                            </ContextMenuItem>
                            <ContextMenuItem onClick={() => runAndClose(() => onDeleteVariation?.(variationsIndex,layer ?? null))}>
                                <span>
                                    <FontAwesomeIcon
                                        icon={faTrashCan}
                                        className="me-2"
                                        size="sm"
                                    />
                                    {t('menu.deleteVariation')}
                                </span>
                            </ContextMenuItem>
                            <ContextMenuItem onClick={() => runAndClose(() => onDownloadAllVariations?.(allVariations))}>
                                <span>
                                    <FontAwesomeIcon
                                        icon={faDownload}
                                        className="me-2"
                                        size="sm"
                                    />
                                    {t('menu.downloadAllVariations')}
                                </span>
                            </ContextMenuItem>
                        </>
                    :   <></>
                }
            </>
        </EffectThemeContainer>
        </div>
    )

    return createPortal(menuElement, document.body);
};

export const ContextMenuImageGallery = ({
    index,
    image,
    baseImage, 
    contextMenu,
    folder,
    setContextMenu,
    onRemoveItem,
    onAddAsImageLayer,
    onOpenProject
}: ContextMenuImageGalleryProps) => {
    const { t } = useTranslation('imageStudioToolbar');
    const menuRef = useRef<HTMLDivElement | null>(null);

    const [menuPlacement, setMenuPlacement] = useState({
        x: contextMenu.x,
        y: contextMenu.y,
        openLeft: false,
        openUp: false
    });

    const closeContextMenu = () => {
        setContextMenu((prev) => ({ ...prev, visible: false }));
    };

    const runAndClose = (callback?: () => void | Promise<void>) => {
        void callback?.();
        closeContextMenu();
    };

    useEffect(() => {
        const onMouseDown = (e: MouseEvent) => {
        if (menuRef.current?.contains(e.target as Node)) return;
        closeContextMenu();
        };

        const onKeyDown = (e: KeyboardEvent) => {
        if (e.key === "Escape") closeContextMenu();
        };

        window.addEventListener("mousedown", onMouseDown);
        window.addEventListener("keydown", onKeyDown);

        return () => {
        window.removeEventListener("mousedown", onMouseDown);
        window.removeEventListener("keydown", onKeyDown);
        };
    }, []);

    useLayoutEffect(() => {
        if (!contextMenu.visible) return;

        const menu = menuRef.current;
        if (!menu) return;

        const padding = 8;
        const submenuWidth = 240;

        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        const menuWidth = menu.offsetWidth;
        const menuHeight = menu.offsetHeight;

        const openLeft =
            contextMenu.x + menuWidth + submenuWidth + padding > viewportWidth;

        const openUp =
            contextMenu.y + menuHeight + padding > viewportHeight;

        const nextX = Math.max(
            padding,
            Math.min(contextMenu.x, viewportWidth - menuWidth - padding)
        );

        const nextY = Math.max(
            padding,
            Math.min(contextMenu.y, viewportHeight - menuHeight - padding)
        );

        setMenuPlacement({
            x: nextX,
            y: nextY,
            openLeft,
            openUp
        });
    }, [contextMenu.visible, contextMenu.x, contextMenu.y]);

    if (!contextMenu.visible) return null;

    const menuElement = (
        <div
            ref={menuRef}
            className={[
                "context-menu-position",
                menuPlacement.openLeft ? "context-menu-open-left" : "",
                menuPlacement.openUp ? "context-menu-open-up" : ""
            ].join(" ")}
            style={{
                left: menuPlacement.x,
                top: menuPlacement.y
            }}
            onContextMenu={(e) => e.preventDefault()}
            onMouseDown={(e) => e.stopPropagation()}
        >
        <EffectThemeContainer containerClass="context-menu-container">
            <>
            <ContextMenuItem onClick={() => runAndClose(() => onOpenProject(index,image))}>
                <span>
                    <FontAwesomeIcon
                        className="me-2"
                        icon={faImage} 
                        size="sm"
                    />{t('menu.openProject')}
                </span>
            </ContextMenuItem>
            {
                baseImage && (
                    <ContextMenuItem onClick={() => runAndClose(() => onAddAsImageLayer(index,image))}>
                        <span>
                            <FontAwesomeIcon
                                className="me-2"
                                icon={faPlus} 
                                size="sm"
                            />{t('menu.addItemAsImageLayer')}
                        </span>
                    </ContextMenuItem>
                )
            }
            
            {folder !== "History" && (
                <ContextMenuItem onClick={() => runAndClose(() => onRemoveItem(index, "History"))}>
                    <span>
                        <FontAwesomeIcon
                            className="me-2"
                            icon={faX} 
                            size="sm"
                        />{t('menu.removeFromHistory')}
                    </span>
                </ContextMenuItem>
            )}

            {folder !== "Current" && (
                <ContextMenuItem onClick={() => runAndClose(() => onRemoveItem(index, "Current"))}>
                    <span>
                        <FontAwesomeIcon
                            className="me-2"
                            icon={faX} 
                            size="sm"
                        /> {t('menu.removeFromCurrent')}
                    </span>
                </ContextMenuItem>
            )}

            <ContextMenuItem onClick={() => runAndClose(() => onRemoveItem(index, "All"))}>
                <span>
                    <FontAwesomeIcon
                        className="me-2"
                        icon={faTrashCan} 
                        size="sm"
                    />{t('menu.deletePermanently')}
                </span>
            </ContextMenuItem>
                
            </>
        </EffectThemeContainer>
        </div>
    );

    return createPortal(menuElement, document.body);
};